'use client'

import { useCallback, useRef, useState } from 'react'

import { ChevronDownIcon, XIcon } from 'lucide-react'

import { Button } from '~/components/component-library'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover'
import { sendTrackingData } from '~/utils/tracking'

export const FilterPopoverButton = ({
  label,
  activeLabel,
  isAppliedEmpty,
  isLocalEmpty,
  isInvalid,
  hasChanged,
  onApply,
  onReset,
  onClear,
  onClose,
  children,
}: {
  label: string
  activeLabel: React.ReactNode
  isAppliedEmpty: boolean
  isLocalEmpty: boolean
  isInvalid?: boolean
  hasChanged: boolean
  onApply: () => void
  onReset: () => void
  onClear: () => void
  onClose: () => void
  children: React.ReactNode | ((focusAction: () => void) => React.ReactNode)
}) => {
  const [open, setOpen] = useState(false)
  const openedByKeyboardRef = useRef(false)
  const applyRef = useRef<HTMLButtonElement>(null)
  const resetRef = useRef<HTMLButtonElement>(null)

  const focusAction = useCallback(() => {
    requestAnimationFrame(() => {
      if (applyRef.current && !applyRef.current.disabled) {
        applyRef.current.focus()
      } else {
        resetRef.current?.focus()
      }
    })
  }, [])

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          onClose()
          openedByKeyboardRef.current = false
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="small"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openedByKeyboardRef.current = true
            }
            if (e.key === 'Backspace' && !isAppliedEmpty) {
              e.preventDefault()
              onClear()
            }
          }}
          className={
            !isAppliedEmpty ? 'bg-gray-100 dark:bg-gray-800' : undefined
          }
          suffix={
            !isAppliedEmpty ? (
              <div
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation()
                  onClear()
                }}
                className="-mr-1 flex items-center justify-center rounded-full p-0.5 text-primary hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <XIcon className="h-4 w-4" />
              </div>
            ) : (
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-primary" />
            )
          }
        >
          {!isAppliedEmpty ? (
            <span className="max-w-48 truncate">{activeLabel}</span>
          ) : (
            label
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[22rem] md:w-[22rem]"
        align="start"
        preventAutoFocus={!openedByKeyboardRef.current}
      >
        <div className="flex flex-col gap-5">
          {typeof children === 'function' ? children(focusAction) : children}
          <div className="flex items-center justify-end gap-2">
            {!isLocalEmpty && (
              <Button
                ref={resetRef}
                type="button"
                variant="tertiary"
                size="small"
                onClick={() => {
                  onReset()
                  setOpen(false)
                }}
              >
                Reset
              </Button>
            )}
            <Button
              ref={applyRef}
              type="button"
              size="small"
              onClick={() => {
                onApply()
                sendTrackingData('accountingFilterApplied', { filter: label })
                setOpen(false)
              }}
              isDisabled={isInvalid || !hasChanged}
              shortcut={
                open && hasChanged && !isInvalid
                  ? {
                      key: 'meta+enter',
                      callback: () => {
                        onApply()
                        setOpen(false)
                      },
                    }
                  : undefined
              }
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
